import React, { useLayoutEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { DirectionsChooser } from '../DirectionsChooser';
import { HeroCallPicker } from './HeroCallPicker';
import { photoUrl } from '../../data/photos';

export const HomeHero = () => {
  const headlineRef = useRef(null);

  useLayoutEffect(() => {
    const headline = headlineRef.current;
    const hero = headline.closest('.reference-hero');
    const photo = hero.querySelector('.reference-hero-photo');
    const positionHeadline = () => {
      // Offset geometry ignores transforms: retain the original space below the headline.
      let naturalTop = 0;
      let element = headline;
      while (element && element !== hero) {
        naturalTop += element.offsetTop;
        element = element.offsetParent;
      }
      if (element === hero) {
        let targetTop = 48;
        if (photo.naturalWidth && photo.naturalHeight) {
          // Keep the existing crew-photo inspection sign clear as its cover crop changes.
          const scale = Math.max(hero.clientWidth / photo.naturalWidth, hero.clientHeight / photo.naturalHeight);
          const imageWidth = photo.naturalWidth * scale;
          const imageHeight = photo.naturalHeight * scale;
          const imageLeft = (hero.clientWidth - imageWidth) * 0.54;
          const signLeft = imageLeft + imageWidth * (130 / 1339);
          const signRight = imageLeft + imageWidth * (230 / 1339);
          const signBottom = (hero.clientHeight - imageHeight) / 2 + imageHeight * (246 / 944);
          const headlineLeft = headline.getBoundingClientRect().left - hero.getBoundingClientRect().left;
          if (headlineLeft < signRight && headlineLeft + headline.offsetWidth > signLeft) {
            targetTop = Math.max(targetTop, Math.ceil(signBottom + 2));
          }
        }
        headline.style.setProperty('--hero-slogan-shift', `${Math.min(0, targetTop - naturalTop)}px`);
      }
    };
    positionHeadline();
    const observer = new ResizeObserver(positionHeadline);
    observer.observe(hero);
    observer.observe(headline.parentElement);
    photo.addEventListener('load', positionHeadline);
    return () => {
      observer.disconnect();
      photo.removeEventListener('load', positionHeadline);
    };
  }, []);

  return (
  <>
    <section className="reference-hero" data-testid="home-hero">
      <img className="reference-hero-photo" src={photoUrl('05')} alt="Performance Lube 915 crew outside the Gateway shop" fetchPriority="high" />
      <div className="reference-hero-shade" />
      <span className="hero-area-code" aria-hidden="true">915</span>
      <div className="site-container reference-hero-content">
        <h1 ref={headlineRef} className="hero-slogan" data-testid="home-title">WE KEEP<br /><span data-testid="hero-slogan-highlight">EL PASO MOVING.</span></h1>
        <p className="reference-tagline" data-testid="hero-tagline">Oil changes in a flash. Brakes, rotors, injectors, tires — fixed right and priced straight. Walk in, roll out.</p>
        <div className="reference-actions hero-actions">
          <HeroCallPicker />
          <DirectionsChooser prefix="hero-"><Button type="button" className="brand-button outline-button" data-testid="hero-directions-button"><MapPin size={17} aria-hidden="true" />Get directions</Button></DirectionsChooser>
        </div>
        <div className="hero-trust" data-testid="hero-trust">
          <span data-testid="hero-google-rating"><span className="review-stars" aria-label="5 stars">★★★★★</span> <b>4.9</b> on Google</span>
          <span data-testid="hero-review-count"><b>130+</b> verified reviews</span>
          <span><b>2</b> El Paso shops</span><span>Open <b>6 days</b> a week</span>
        </div>
      </div>
    </section>
    <div className="service-ticker-clip" data-testid="home-service-ticker"><div className="service-ticker"><div className="service-ticker-track">
      {[false, true].map((hidden) => <span key={String(hidden)} aria-hidden={hidden}>EXPRESS OIL CHANGES ✦ BRAKE ROTORS ✦ FUEL INJECTOR CLEANING ✦ TIRE SALES &amp; BALANCING ✦ ENGINE DIAGNOSTICS ✦ BATTERIES ✦&nbsp;</span>)}
    </div></div></div>
  </>
  );
};

export const HomeStats = () => (
  <section className="reference-stats" data-testid="home-stats" aria-label="Performance Lube at a glance"><div className="site-container stats-grid">
    {[
      ['rating', '4.9', 'Google rating across 130+ reviews'],
      ['shops', '2', 'Shops — Gateway & Desert Meadows'],
      ['services', '7+', 'Core services under one roof'],
      ['days', '6', 'Days a week, walk-ins welcome'],
    ].map(([id, value, description]) => <div key={id} data-testid={`home-stat-${id}`}><div className="stat-value" data-testid={`home-stat-${id}-value`}>{value}{id === 'rating' && <span className="review-stars">★</span>}</div><p>{description}</p></div>)}
  </div></section>
);